import React from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../models/RootState";
import { UserDetails } from "../../../models/UserDetails";
import { ColorConfig } from "../../../models/UserDetails";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "../../../hooks/useTranslation";
import { themes } from "./MyProfile";
import CSK from "../../../assets/images/csk.svg";
import DC from "../../../assets/images/dc.svg";
import GT from "../../../assets/images/gt.svg";
import KKR from "../../../assets/images/kkr.svg";
import LSG from "../../../assets/images/lsg.svg";
import MI from "../../../assets/images/mi.svg";
import PK from "../../../assets/images/pk.svg";
import RR from "../../../assets/images/rr.svg";
import RCB from "../../../assets/images/rcb.svg";
import SRH from "../../../assets/images/srh.svg";
import API from "../../../services";
import { toast } from "react-toastify";
import { loadColorConfig } from "../../../constants/common";
import { getDynamicTextColors, updateDynamicTextColors } from "../../../store/authActions";
import { setUserDetails } from "../../../store/authReducer";

type IplThemesProps = {
  userDetails: UserDetails;
  colorConfig: ColorConfig;
  dispatch: Function;
};

type IplTeamKey = "CSK" | "DC" | "GT" | "KKR" | "LSG" | "MI" | "PK" | "RR" | "RCB" | "SRH";

const teams: Array<{ key: IplTeamKey; name: string; logo: string; tagline: string }> = [
  { key: "CSK", name: "Chennai Super Kings", logo: CSK, tagline: "Whistle Podu vibes" },
  { key: "DC", name: "Delhi Capitals", logo: DC, tagline: "Dil Dilli, Dilli Capitals" },
  { key: "GT", name: "Gujarat Titans", logo: GT, tagline: "Titans never back down" },
  { key: "KKR", name: "Kolkata Knight Riders", logo: KKR, tagline: "Korbo, Lorbo, Jeetbo" },
  { key: "LSG", name: "Lucknow Super Giants", logo: LSG, tagline: "Naya Sheher, Nayi Team" },
  { key: "MI", name: "Mumbai Indians", logo: MI, tagline: "Akha Mumbai Khelega" },
  { key: "PK", name: "Punjab Kings", logo: PK, tagline: "Punjabiyan di Shaan" },
  { key: "RR", name: "Rajasthan Royals", logo: RR, tagline: "Halla Bol energy" },
  { key: "RCB", name: "Royal Challengers Bengaluru", logo: RCB, tagline: "Ee Sala Theme Namde" },
  { key: "SRH", name: "Sunrisers Hyderabad", logo: SRH, tagline: "Orange Army feels" },
];

const IplThemes: React.FC<IplThemesProps> = ({ userDetails, colorConfig, dispatch }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { pageTextColorClass, buttonTextColorClass } = getDynamicTextColors(userDetails);

  const handleBackClick = () => {
    navigate(-1);
  };

  const applyBackendTheme = (colors: ColorConfig) => {
    const selectedTheme = {
      inputboxColor: colors.inputBackgroundBox,
      appbgColor: colors.background,
      headerColor: colors.primary,
      filterColor: colors.filterColor,
      tablebg: colors.tableBackground,
      tableHeader: colors.tableHeader,
      tableHeadCellBorder: colors.tableBorder,
      marketTableBg: colors.tableBackground,
      ionColorPrimary: colors.primaryTeamInfo,
      teamInfoBackground: colors.backgroundTeamInfo,
      inputTextColor: colors.text,
      borderColor: colors.primary,
      backgroundCard: colors.tableBackground,
    };
    return selectedTheme;
  };

  const changeTheme = async (theme: string, selectedTheme: any) => {
    try {
      const response = await API.post(`/users/change-user-theme`, {
        theme: theme,
      });

      if (response.data?.status !== "RS_OK") {
        toast.error(response.data?.errorDescription);
        return;
      }

      toast.success(t("profile.messages.themeChangedSuccessfully"));
      loadColorConfig(selectedTheme);

      // Refresh user details so Theme stays in sync
      try {
        const userKey = sessionStorage.getItem("userKey");
        const detailsResp = await API.post(`/users/get-user-details`, { userKey });
        if (detailsResp?.data?.status === "RS_OK" && detailsResp.data?.userDetails) {
          dispatch(setUserDetails(detailsResp.data.userDetails));
        }
      } catch {
        // ignore secondary failure
      }

      setTimeout(() => {
        dispatch(updateDynamicTextColors() as any);
      }, 100);
    } catch (e: any) {
      toast.error(e?.response?.data?.errorDescription || "Failed to change theme");
    }
  };

  const handleTeamThemeClick = async (teamKey: IplTeamKey) => {
    const selectedTheme = themes[teamKey];
    if (!selectedTheme) return;
    await changeTheme(teamKey, selectedTheme);
  };

  const handleBackToDefault = async () => {
    if (!colorConfig || !Object.values(colorConfig).some((v) => v !== "")) return;
    const selectedTheme = applyBackendTheme(colorConfig);
    await changeTheme("default", selectedTheme);
  };

  return (
    <div className="flex flex-col items-center px-3 mt-4 w-full pb-[7rem] py-[4rem] my-profile">
      <header
        className={`flex items-center justify-between w-full max-w-3xl mb-4 text-base font-medium ${
          pageTextColorClass === "dark-text" ? "text-black" : "text-white"
        }`}
      >
        <button
          type="button"
          className="bg-inputbox-color flex p-1 rounded-lg border border-border-color"
          onClick={handleBackClick}
        >
          <ArrowBackIcon
            className="aspect-square w-[18px]"
            style={{ width: "20px", height: "20px" }}
          />
        </button>
        <div className="flex flex-col items-center flex-1 px-2">
          <h2 className="text-lg font-semibold text-center">Game time! Wear your team's look</h2>
          <p className="mt-1 text-xs sm:text-sm opacity-80 text-center">
            Tap your favourite team to instantly restyle like your team.
          </p>
        </div>
        <div className="w-[40px]" />
      </header>

      <div className="w-full max-w-3xl mb-4">
        <button
          type="button"
          onClick={handleBackToDefault}
          className={`w-full px-4 py-2 rounded-xl border border-border-color bg-inputbox-color text-xs sm:text-sm flex items-center justify-center gap-2 ${
            buttonTextColorClass === "dark-text" ? "text-black" : "text-white"
          }`}
        >
          <span>Back to default theme</span>
        </button>
      </div>

      <div className="w-full max-w-3xl grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mt-2">
        {teams.map((team) => {
          const isActive = userDetails?.Theme === team.key;
          return (
            <button
              key={team.key}
              type="button"
              onClick={() => handleTeamThemeClick(team.key)}
              className={`relative flex flex-col items-center justify-between p-2 sm:p-3 rounded-2xl border transition-all duration-200 bg-inputbox-color text-left ${
                isActive
                  ? "border-border-color shadow-lg scale-[1.02]"
                  : "border-slate-700 hover:border-border-color hover:shadow-md"
              }`}
            >
              {isActive && (
                <span className="absolute top-2 right-2 text-green-400">
                  <CheckCircleIcon fontSize="small" />
                </span>
              )}
              <div className="flex flex-col items-center justify-center mb-2">
                <div
                  className={`p-1 border-2 rounded-lg bg-white flex items-center justify-center ${
                    isActive ? "border-border-color" : "border-gray-300"
                  }`}
                >
                  <img
                    src={team.logo}
                    alt={team.name}
                    className="w-12 h-12 sm:w-14 sm:h-14 object-contain"
                  />
                </div>
                <span className="mt-2 text-xs sm:text-sm font-semibold text-center line-clamp-2">
                  {team.name}
                </span>
              </div>
              <p className="mt-1 text-[0.65rem] sm:text-xs opacity-80 text-center">
                {team.tagline}
              </p>
            </button>
          );
        })}
      </div>

      <p className="mt-4 text-[0.65rem] sm:text-xs opacity-70 text-center max-w-3xl">
        You can change your team anytime. Themes update live across the entire app, including
        home, casino, sports and more.
      </p>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    userDetails: state.userDetails?.userDetails,
    colorConfig: state.userDetails?.colorConfig,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    dispatch,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(IplThemes);

